import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'

const BLUE   = '#1877F2'
const NAVY   = '#0B1F3A'
const TEAL   = '#2dd4b0'
const GOLD   = '#F59E0B'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const BORDER = '#E4E6EB'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'

// ── Categories ────────────────────────────────────────────────────────────
const CATEGORIES = [
  { group: 'Men\'s Clothing', items: ['Panjabi','Shirt','T-shirt / Polo','Trouser / Pant','Jeans','Jacket / Blazer','Suit / Sherwani','Jersey / Sportswear','Kurta','Lungi','Shorts','Other menswear'] },
  { group: 'Women\'s Clothing', items: ['Saree','Salwar Kameez','Kurti','Dress / Gown','Abaya','Hijab / Scarf set','Top / Blouse','Skirt','Lehenga','Jacket','Jeans / Trouser','Sportswear','Other womenswear'] },
  { group: 'Kids Clothing', items: ['Boys outfit','Girls outfit','Baby clothing'] },
  { group: 'Footwear', items: ['Shoes / Sneakers','Sandals / Heels','Boots','Slippers'] },
  { group: 'Accessories', items: ['Bag / Handbag','Backpack','Sunglasses','Watch','Jewellery','Belt','Hat / Cap','Wallet','Perfume / Fragrance','Other accessory'] },
]

const CATEGORY_TIPS = {
  'Saree': '⚠️ Sarees involve complex draping and layering. AI may struggle with fine details. For best results use a pre-draped mannequin photo. Results may vary.',
  'Lehenga': '⚠️ Complex garment with multiple pieces. Pre-draped mannequin photo recommended.',
  'Jewellery': '💡 Use a close-up shot on a clean flat surface. Good macro lighting makes a big difference.',
  'Perfume / Fragrance': '💡 Product-only shot works here — no model needed. AI will place it in a lifestyle setting.',
  'Sunglasses': '💡 Close-up portrait style works best. Make sure the product is clearly visible.',
  'Shoes / Sneakers': '💡 Side profile shot of the shoe gives the clearest result.',
  'Sandals / Heels': '💡 Side or slight angle shot works best.',
  'Bag / Handbag': '💡 Front-facing flat shot shows the bag design clearly.',
}

const GARMENT_PROMPT_ADDITIONS = {
  'Saree': 'wearing a beautifully draped saree with matching blouse',
  'Salwar Kameez': 'wearing a complete salwar kameez set',
  'Panjabi': 'wearing a traditional panjabi',
  'Abaya': 'wearing a full length abaya, modest styling',
  'Hijab / Scarf set': 'wearing hijab with matching outfit, modest styling',
  'Shoes / Sneakers': 'full body shot showing footwear clearly',
  'Sandals / Heels': 'full body or lower body shot showing footwear clearly',
  'Bag / Handbag': 'holding the bag naturally, bag clearly visible',
  'Sunglasses': 'close-up portrait with sunglasses, face clearly visible',
  'Jewellery': 'close-up portrait highlighting jewellery in detail',
  'Watch': 'wrist close-up showing watch detail',
  'Perfume / Fragrance': 'holding perfume bottle elegantly in lifestyle setting',
}

// ── Model Options ─────────────────────────────────────────────────────────
const GENDERS    = ['Woman','Man','Teen girl','Teen boy','Young girl (child)','Young boy (child)']
const AGE_GROUPS = ['Teen (13-17)','Young adult (18-25)','Adult (26-35)','Mid adult (36-45)','Mature (46+)']
const SKIN_TONES = ['Very fair','Fair','Wheatish','Light brown','Brown','Dark brown','Deep dark']
const ETHNICITIES = ['Bangladeshi','Indian','Pakistani','South Asian (mixed)','Arab / Middle Eastern','East Asian','South East Asian','White American','White European','White Australian','African','Latin / Hispanic','Chakma / Indigenous BD']
const MODEL_STYLES = ['Casual everyday','Smart professional','Trendy / fashionable','Traditional / ethnic','Sporty / active','Elegant / formal','Streetwear / cool','Modest / conservative']
const POSES = ['Standing front','Standing — 3/4 angle','Walking','Sitting','Leaning / relaxed','Close-up portrait','Full body']
const SETTINGS = ['Plain white studio','Plain grey studio','Plain black studio','Soft beige / cream','Outdoor natural light','Urban street','Office / corporate','Home / indoor lifestyle','Festive — Eid','Festive — Pohela Boishakh','Festive — Wedding','Beach / coastal','Garden / park','Market / bazaar','Cafe / restaurant','Night / evening city']
const BODY_TYPES = ['Slim / lean','Average / regular','Athletic','Plus size']

// ── Library Segments ──────────────────────────────────────────────────────
const LIBRARY_SEGMENTS = [
  'BD Woman — Young','BD Woman — Adult','BD Woman — Mature',
  'BD Man — Young','BD Man — Adult','BD Man — Mature',
  'Hijabi Woman — Young','Hijabi Woman — Adult',
  'Burkhabi Woman','Chakma / Indigenous',
  'South Asian Woman','South Asian Man',
  'Arab Woman','Arab Man',
  'Western Woman','Western Man',
  'Teen Girl','Teen Boy',
]

// ── Pack definitions ──────────────────────────────────────────────────────
const PACKS_BDT = [
  { name: 'Starter',  credits: 8,  price: 200,  saving: null },
  { name: 'Standard', credits: 20, price: 475,  saving: 25 },
  { name: 'Popular',  credits: 40, price: 900,  saving: 100, best: true },
  { name: 'Pro',      credits: 80, price: 1700, saving: 300 },
]
const PACKS_USD = [
  { name: 'Starter',  credits: 8,  price: 3,  saving: null },
  { name: 'Standard', credits: 20, price: 7,  saving: 0.5 },
  { name: 'Popular',  credits: 40, price: 13, saving: 2, best: true },
  { name: 'Pro',      credits: 80, price: 24, saving: 6 },
]

function Sel({ label, value, onChange, options, optional }) {
  return (
    <div>
      <label style={{ fontSize: '11px', fontWeight: 700, color: MUTED, display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}{optional && <span style={{ fontWeight: 400, marginLeft: '4px', textTransform: 'none' }}>optional</span>}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '8px', padding: '8px 10px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, sans-serif' }}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}

function AIModelPhotos() {
  const { seller, getSubscriptionPlan } = useAuth()
  const { showToast } = useToast()
  // const canvasRef    = useRef(null)
  const logoInputRef = useRef(null)

  const plan = getSubscriptionPlan?.() || 'starter'
  const isBD = (seller?.seller_currency || 'BDT') === 'BDT'
  const sym  = isBD ? '৳' : '$'
  const packs = isBD ? PACKS_BDT : PACKS_USD

  // Credits
  const [credits, setCredits]         = useState({ free_credits: 0, paid_credits: 0 })
  const [creditsLoading, setCreditsLoading] = useState(true)

  // Generation
  const [activeTab, setActiveTab]     = useState('generate') // generate | library
  // const [productFile, setProductFile] = useState(null)
  const [productPreview, setProductPreview] = useState(null)
  const [productImageUrl, setProductImageUrl] = useState(null)
  const [uploadingProduct, setUploadingProduct] = useState(false)
  const [category, setCategory]       = useState('')
  const [gender, setGender]           = useState('')
  const [ageGroup, setAgeGroup]       = useState('')
  const [skinTone, setSkinTone]       = useState('')
  const [ethnicity, setEthnicity]     = useState('')
  const [modelStyle, setModelStyle]   = useState('')
  const [pose, setPose]               = useState('')
  const [setting, setSetting]         = useState('')
  const [bodyType, setBodyType]       = useState('')
  const [customContext, setCustomContext] = useState('')
  const [finalPrompt, setFinalPrompt] = useState('')
  const [promptEdited, setPromptEdited] = useState(false)
  const [selectedLibModel, setSelectedLibModel] = useState(null)
  const [libFilter, setLibFilter]     = useState('')
  const [libraryModels, setLibraryModels] = useState([])
  const [libLoading, setLibLoading]   = useState(false)

  // Generation state
  const [generating, setGenerating]   = useState(false)
  const [generatingMsg, setGeneratingMsg] = useState('')
  const [outputUrl, setOutputUrl]     = useState(null)
  // const [generationId, setGenerationId] = useState(null)
  // const [pollInterval, setPollInterval] = useState(null)

  // Watermark
  const [wmText, setWmText]           = useState(true)
  const [wmLogo, setWmLogo]           = useState(false)
  // wmLogoFile removed - unused
  const [wmLogoImg, setWmLogoImg]     = useState(null)
  const [wmOpacity, setWmOpacity]     = useState(15)
  const [wmPosition, setWmPosition]   = useState('bottom-right') // top-left/top-right/bottom-left/bottom-right/center
  const [wmSize, setWmSize]           = useState(80)

  // Packs / payment
  const [payingPack, setPayingPack]   = useState(null)
  const [payLoading, setPayLoading]   = useState(false)

  // History
  const [history, setHistory]         = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Disclaimer
  const [disclaimerDone, setDisclaimerDone] = useState(false)

  useEffect(() => {
    fetchCredits()
    fetchHistory()
    checkDisclaimer()
  }, []) // eslint-disable-line

  useEffect(() => {
    if (activeTab === 'library') fetchLibraryModels()
  }, [activeTab]) // eslint-disable-line

  useEffect(() => {
    if (!promptEdited) buildPrompt()
  }, [category, gender, ageGroup, skinTone, ethnicity, modelStyle, pose, setting, bodyType, customContext]) // eslint-disable-line

  const fetchCredits = async () => {
    if (!seller?.id) return
    setCreditsLoading(true)
    const { data } = await supabase.from('seller_ai_credits').select('*').eq('seller_id', seller.id).single()
    if (data) setCredits(data)
    else {
      // Init credits record with free credits based on plan
      const freeMap = { pro: 3, business: 10 }
      const free = freeMap[plan] || 0
      await supabase.from('seller_ai_credits').upsert({ seller_id: seller.id, free_credits: free, paid_credits: 0, free_reset_at: new Date().toISOString() }, { onConflict: 'seller_id' })
      setCredits({ free_credits: free, paid_credits: 0 })
    }
    setCreditsLoading(false)
  }

  const fetchHistory = async () => {
    if (!seller?.id) return
    setHistoryLoading(true)
    const { data } = await supabase.from('ai_generation_history').select('*').eq('seller_id', seller.id).order('created_at', { ascending: false }).limit(20)
    setHistory(data || [])
    setHistoryLoading(false)
  }

  const fetchLibraryModels = async () => {
    setLibLoading(true)
    const { data } = await supabase.from('ai_library_models').select('*').eq('is_active', true).order('segment').order('sort_order')
    setLibraryModels(data || [])
    setLibLoading(false)
  }

  const checkDisclaimer = async () => {
    if (!seller?.id) return
    const { data } = await supabase.from('sellers').select('ai_disclaimer_accepted').eq('id', seller.id).single()
    setDisclaimerDone(data?.ai_disclaimer_accepted || false)
  }

  const acceptDisclaimer = async () => {
    await supabase.from('sellers').update({ ai_disclaimer_accepted: true }).eq('id', seller.id)
    setDisclaimerDone(true)
  }

  const buildPrompt = () => {
    const parts = []
    if (pose) parts.push(pose + ' shot')
    else parts.push('Full body shot')
    if (ageGroup) parts.push(ageGroup.toLowerCase())
    if (ethnicity) parts.push(ethnicity)
    if (skinTone) parts.push(skinTone.toLowerCase() + ' skin tone')
    if (gender) parts.push(gender.toLowerCase())
    if (bodyType && bodyType !== 'Average / regular') parts.push(bodyType.toLowerCase() + ' build')
    if (modelStyle) parts.push(modelStyle.toLowerCase() + ' style')
    const garmentAddition = GARMENT_PROMPT_ADDITIONS[category]
    if (garmentAddition) parts.push(garmentAddition)
    if (setting) parts.push(setting.toLowerCase() + ' background')
    parts.push('professional fashion photography lighting')
    parts.push('sharp fabric and product detail')
    if (customContext) parts.push(customContext)
    setFinalPrompt(parts.join(', '))
  }

  const handleProductUpload = async (file) => {
    if (!file) return
    setProductPreview(URL.createObjectURL(file))
    setUploadingProduct(true)
    try {
      const ext      = file.name.split('.').pop() || 'jpg'
      const fileName = `ai-inputs/${seller.id}/${Date.now()}.${ext}`
      await supabase.storage.from('ai-generations').upload(fileName, file, { upsert: true, contentType: file.type })
      const { data: urlData } = supabase.storage.from('ai-generations').getPublicUrl(fileName)
      setProductImageUrl(urlData?.publicUrl)
    } catch { showToast('Upload failed', 'error') }
    setUploadingProduct(false)
  }

  const handleGenerate = async () => {
    if (!productImageUrl) { showToast('Please upload your product photo first', 'error'); return }
    if (!finalPrompt)     { showToast('Please complete the options to build your prompt', 'error'); return }
    const total = (credits.free_credits || 0) + (credits.paid_credits || 0)
    if (total < 1) { showToast('No studio credits remaining. Top up to continue.', 'error'); return }

    setGenerating(true)
    setOutputUrl(null)
    setGeneratingMsg('Lights on...')
    setTimeout(() => setGeneratingMsg('Camera rolling...'), 2000)
    setTimeout(() => setGeneratingMsg('AI is working its magic...'), 5000)

    try {
      const { data, error } = await supabase.functions.invoke('generate-ai-model', {
        body: { seller_id: seller.id, product_image_url: productImageUrl, prompt: finalPrompt, category, library_model_id: selectedLibModel?.id || null }
      })
      if (error || data?.error) {
        if (data?.error === 'no_credits') showToast('No studio credits remaining. Top up to continue.', 'error')
        else showToast('Generation failed. Please try again.', 'error')
        setGenerating(false); return
      }

      // Poll for result
      const interval = setInterval(async () => {
        const { data: pollData } = await supabase.functions.invoke('poll-ai-status', {
          body: { prediction_id: data.prediction_id, generation_id: data.generation_id, seller_id: seller.id }
        })
        if (pollData?.status === 'completed') {
          clearInterval(interval)
          setOutputUrl(pollData.output_url)
          setGenerating(false)
          setGeneratingMsg('')
          fetchCredits()
          fetchHistory()
          showToast('Your studio shot is ready!')
        } else if (pollData?.status === 'failed') {
          clearInterval(interval)
          setGenerating(false)
          setGeneratingMsg('')
          showToast('Generation failed. Credit refunded.', 'error')
          fetchCredits()
        }
      }, 3000)
    } catch {
      setGenerating(false)
      showToast('Something went wrong. Please try again.', 'error')
    }
  }

  // ── Watermark canvas ───────────────────────────────────────────────────
  const applyWatermark = useCallback((mode) => {
    if (!outputUrl) return
    const canvas  = document.createElement('canvas')
    const ctx     = canvas.getContext('2d')
    const img     = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      canvas.width  = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)

      if (mode === 'clean') {
        canvas.toBlob(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'studio-shot.jpg'; a.click() })
        return
      }

      // Text watermark
      if ((mode === 'text' || mode === 'both') && wmText) {
        ctx.save()
        ctx.globalAlpha = wmOpacity / 100
        ctx.fillStyle   = '#FFFFFF'
        const fontSize  = Math.round(img.width * 0.03)
        ctx.font        = `bold ${fontSize}px Space Grotesk, Arial`
        ctx.textAlign   = 'center'
        // Diagonal repeat pattern
        ctx.translate(img.width / 2, img.height / 2)
        ctx.rotate(-Math.PI / 6)
        const shopText = seller?.shop_name || 'PageConnect'
        for (let y = -img.height; y < img.height; y += fontSize * 4) {
          for (let x = -img.width; x < img.width; x += fontSize * 10) {
            ctx.fillText(shopText, x, y)
          }
        }
        ctx.restore()
      }

      // Logo watermark
      if ((mode === 'logo' || mode === 'both') && wmLogoImg) {
        const logoImg = new Image()
        logoImg.onload = () => {
          ctx.save()
          ctx.globalAlpha = wmOpacity / 100
          const logoW = wmSize * 2
          const logoH = (logoImg.height / logoImg.width) * logoW
          const pad   = 20
          let lx, ly
          switch (wmPosition) {
            case 'top-left':     lx = pad; ly = pad; break
            case 'top-right':    lx = img.width - logoW - pad; ly = pad; break
            case 'bottom-left':  lx = pad; ly = img.height - logoH - pad; break
            case 'bottom-right': lx = img.width - logoW - pad; ly = img.height - logoH - pad; break
            default:             lx = (img.width - logoW) / 2; ly = (img.height - logoH) / 2
          }
          ctx.drawImage(logoImg, lx, ly, logoW, logoH)
          ctx.restore()
          canvas.toBlob(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'studio-shot-watermarked.jpg'; a.click() })
        }
        logoImg.src = wmLogoImg
      } else {
        canvas.toBlob(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'studio-shot.jpg'; a.click() })
      }
    }
    img.src = outputUrl
  }, [outputUrl, wmText, wmLogoImg, wmOpacity, wmPosition, wmSize, seller])

  // ── Buy pack ───────────────────────────────────────────────────────────
  const handleBuyPack = async (pack) => {
    setPayingPack(pack.name)
    setPayLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { seller_id: seller.id, seller_email: seller.email || '', seller_name: seller.shop_name || '', seller_phone: seller.phone || '', plan_key: 'ai_credits', plan_label: 'AI Studio — ' + pack.name + ' (' + pack.credits + ' shots)', amount: pack.price, currency: isBD ? 'BDT' : 'USD', billing_period: 'one_time', ai_pack: true, ai_credits: pack.credits, ai_pack_name: pack.name }
      })
      if (error || !data?.payment_url) throw new Error('Payment failed to start')
      window.location.href = data.payment_url
    } catch (err) {
      showToast(err.message || 'Could not start payment', 'error')
      setPayingPack(null)
      setPayLoading(false)
    }
  }

  const totalCredits = (credits.free_credits || 0) + (credits.paid_credits || 0)
  const planFreeMax  = plan === 'business' ? 10 : plan === 'pro' ? 3 : 0
  const filteredLib  = libFilter ? libraryModels.filter(m => m.segment === libFilter) : libraryModels

  const selectStyles = {
    border: '1px solid ' + BORDER, borderRadius: '8px', padding: '8px 10px',
    fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT,
    fontFamily: 'Space Grotesk, sans-serif', width: '100%',
  }

  return (
    <div className="flex-1 overflow-x-hidden" style={{ background: BG, fontFamily: 'Space Grotesk, sans-serif' }}>
      <Topbar title="PageConnect AI Studio" />

      {/* Payment loading overlay */}
      {payLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: WHITE, animation: 'spin 0.8s linear infinite' }} />
          <div style={{ color: WHITE, fontSize: '15px', fontWeight: 600 }}>Taking you to payment...</div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Please do not close this tab</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {/* Disclaimer modal */}
      {!disclaimerDone && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '18px', padding: '28px', maxWidth: '480px', width: '100%' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: TEXT, marginBottom: '6px' }}>Before you step on set</div>
            <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.75, marginBottom: '20px' }}>
              AI-generated results depend entirely on the quality of your product photo and prompt. Results may not match exact expectations — this is standard across all AI generation tools.<br /><br />
              <strong style={{ color: TEXT }}>You are responsible</strong> for what you upload. Do not upload counterfeit products, trademarked logos or copyrighted material. AI-generated images reflecting such content may violate Facebook, Instagram and other platform policies, leading to account restrictions.<br /><br />
              PageConnect AI Studio is a generation tool. We take no liability for uploaded content or generated images.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px 14px', background: '#FFFBEB', borderRadius: '10px', border: '1px solid #FDE68A' }}>
              <span style={{ fontSize: '13px', color: AMBER }}>⚠️</span>
              <span style={{ fontSize: '12.5px', color: TEXT, lineHeight: 1.6 }}>Credits used for generation are non-refundable regardless of output quality. Use credits wisely and test prompts carefully.</span>
            </div>
            <button onClick={acceptDisclaimer} style={{ width: '100%', padding: '13px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '14px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
              I understand — take me to the studio
            </button>
          </div>
        </div>
      )}

      <div style={{ padding: '0 0 32px' }}>

        {/* ── Hero banner ──────────────────────────────────────────────────── */}
        <div style={{ background: NAVY, padding: '28px 28px 24px', marginBottom: '20px', position: 'relative', overflow: 'hidden' }}>
          {/* Spotlight effect */}
          <div style={{ position: 'absolute', top: '-60px', right: '10%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '-40px', left: '30%', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(45,212,176,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <span style={{ fontSize: '22px' }}>🎬</span>
                <div style={{ fontSize: '22px', fontWeight: 900, color: WHITE, letterSpacing: '-0.4px' }}>PageConnect AI Studio</div>
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: GOLD + '20', color: GOLD, border: '1px solid ' + GOLD + '40' }}>BETA</span>
              </div>
              <div style={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.6)', marginBottom: '16px', lineHeight: 1.6 }}>
                Your products. Professional models. Studio quality — in seconds.
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {[{ label: '🎯 Product to model', desc: 'One photo in. Professional shot out.' }, { label: '⚡ Instant results', desc: '15-30 seconds per shot' }, { label: '💎 Studio quality', desc: 'Professional lighting and staging' }].map(f => (
                  <div key={f.label} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '10px 14px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: WHITE }}>{f.label}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Credit indicator */}
            <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: '14px', padding: '16px 20px', border: '1px solid rgba(255,255,255,0.12)', minWidth: '180px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: TEAL, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Studio credits</div>
              {creditsLoading ? (
                <div style={{ fontSize: '28px', fontWeight: 900, color: WHITE }}>—</div>
              ) : (
                <>
                  <div style={{ fontSize: '32px', fontWeight: 900, color: totalCredits === 0 ? RED : WHITE, lineHeight: 1 }}>{totalCredits}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                    {credits.free_credits} free · {credits.paid_credits} purchased
                  </div>
                  {planFreeMax > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ width: '100%', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '2px', background: TEAL, width: Math.min(100, (credits.free_credits / planFreeMax) * 100) + '%' }} />
                      </div>
                      <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>{credits.free_credits}/{planFreeMax} free this month</div>
                    </div>
                  )}
                  {totalCredits === 0 && (
                    <button onClick={() => document.getElementById('studio-packs')?.scrollIntoView({ behavior: 'smooth' })}
                      style={{ marginTop: '10px', padding: '7px 14px', borderRadius: '8px', background: GOLD, border: 'none', fontSize: '11.5px', fontWeight: 700, color: NAVY, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif', width: '100%' }}>
                      Top up the studio ↓
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: '0 20px' }}>

          {/* ── How it works ──────────────────────────────────────────────── */}
          <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '20px', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>How the studio works</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }} className="how-grid">
              {[
                { icon: '📸', step: '1', title: 'Drop your product on set', desc: 'Upload your product photo. Flat-lay or hanger on a clean background works best. The better the photo, the better the output.' },
                { icon: '🎭', step: '2', title: 'Cast and set the scene', desc: 'Pick your model type, background, setting and write your brief. Add a custom context for specific details like shoes, jewellery or scene mood.' },
                { icon: '🎬', step: '3', title: 'Say Action!', desc: 'Review your prompt, hit generate. Your studio shot is ready in 15-30 seconds. Download clean or with your watermark.' },
              ].map(s => (
                <div key={s.step} style={{ background: BG, borderRadius: '10px', padding: '16px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{s.icon}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: WHITE, flexShrink: 0 }}>{s.step}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{s.title}</div>
                  </div>
                  <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.6 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Sample results ────────────────────────────────────────────── */}
          <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>What the studio produces</div>
              <span style={{ fontSize: '11px', color: MUTED }}>Upload samples in AdminSettings → AI Studio</span>
            </div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '14px' }}>Sample shots from PageConnect AI Studio across different product categories</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }} className="samples-grid">
              {['Salwar Kameez','Panjabi','Shoes','Handbag','Jewellery'].map(cat => (
                <div key={cat} style={{ aspectRatio: '3/4', background: BG, borderRadius: '10px', border: '2px dashed ' + BORDER, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <div style={{ fontSize: '24px' }}>📷</div>
                  <div style={{ fontSize: '11px', color: MUTED, textAlign: 'center', padding: '0 8px' }}>{cat}</div>
                  <div style={{ fontSize: '10px', color: '#B0B3B8', textAlign: 'center' }}>Sample coming</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Studio disclaimer banner ──────────────────────────────────── */}
          <div style={{ background: '#FFF8E6', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '12px', color: '#92400E', lineHeight: 1.7 }}>
            <strong>⚖️ Studio Policy —</strong> You are the director. You are responsible for what goes on set. Do not upload counterfeit products, branded logos you do not own, or copyrighted material. AI results may vary — this is standard across all AI tools. PageConnect AI Studio is a generation tool and takes no liability for uploaded content or generated images.
          </div>

          {/* ── Main studio — controls + preview ─────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px', alignItems: 'start' }} className="studio-grid">

            {/* LEFT — The Set */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: '4px', padding: '4px', background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px' }}>
                {[{ key: 'generate', label: '⚡ Quick Generate' }, { key: 'library', label: '🎭 Use Library Model' }].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif', background: activeTab === tab.key ? BLUE : 'transparent', color: activeTab === tab.key ? WHITE : MUTED }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Product upload */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '12px' }}>📦 Drop your product on set</div>

                {/* Upload area */}
                <div
                  onClick={() => document.getElementById('product-upload').click()}
                  style={{ border: '2px dashed ' + (productPreview ? BLUE : BORDER), borderRadius: '10px', padding: '20px', textAlign: 'center', cursor: 'pointer', background: productPreview ? '#E7F3FF' : BG, marginBottom: '12px', transition: 'all 0.15s' }}>
                  {productPreview ? (
                    <img src={productPreview} alt="product" style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }} />
                  ) : (
                    <>
                      <div style={{ fontSize: '28px', marginBottom: '8px' }}>📸</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>Click to upload product photo</div>
                      <div style={{ fontSize: '11.5px', color: MUTED }}>JPG, PNG — max 10MB</div>
                    </>
                  )}
                </div>
                <input id="product-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleProductUpload(e.target.files?.[0])} />
                {uploadingProduct && <div style={{ fontSize: '12px', color: BLUE, textAlign: 'center', marginBottom: '8px' }}>Uploading to studio...</div>}

                {/* Production notes */}
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '9px', padding: '12px 14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: GREEN, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>📋 Production notes — for best results</div>
                  {[
                    '✓  Clean background — white, grey or plain surface',
                    '✓  Good lighting — no harsh shadows',
                    '✓  Full product visible — no cropping',
                    '✓  Hanger or flat-lay works best for clothing',
                    '✓  High resolution — minimum 800px wide',
                    '✗  Avoid: messy backgrounds, folded items, dark photos',
                  ].map((tip, i) => (
                    <div key={i} style={{ fontSize: '11.5px', color: i < 5 ? '#065F46' : RED, marginBottom: '3px' }}>{tip}</div>
                  ))}
                </div>

                {/* Category-specific warning */}
                {category && CATEGORY_TIPS[category] && (
                  <div style={{ marginTop: '10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '9px', padding: '10px 12px', fontSize: '12px', color: AMBER }}>
                    {CATEGORY_TIPS[category]}
                  </div>
                )}
              </div>

              {/* Category */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '12px' }}>🏷️ Product category</div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: MUTED, display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</label>
                  <select value={category} onChange={e => { setCategory(e.target.value); setPromptEdited(false) }} style={selectStyles}>
                    <option value="">Select product category...</option>
                    {CATEGORIES.map(g => (
                      <optgroup key={g.group} label={g.group}>
                        {g.items.map(i => <option key={i} value={i}>{i}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>

              {/* Library model picker */}
              {activeTab === 'library' && (
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '12px' }}>🎭 Cast your model</div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: MUTED, display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filter by segment</label>
                    <select value={libFilter} onChange={e => setLibFilter(e.target.value)} style={selectStyles}>
                      <option value="">All models</option>
                      {LIBRARY_SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  {libLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: MUTED, fontSize: '12px' }}>Loading model library...</div>
                  ) : libraryModels.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', background: BG, borderRadius: '10px' }}>
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>🎭</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>Library coming soon</div>
                      <div style={{ fontSize: '12px', color: MUTED }}>We are generating 72 diverse models. Use Quick Generate for now.</div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                      {filteredLib.map(model => (
                        <div key={model.id} onClick={() => setSelectedLibModel(selectedLibModel?.id === model.id ? null : model)}
                          style={{ borderRadius: '9px', border: '2px solid ' + (selectedLibModel?.id === model.id ? BLUE : BORDER), cursor: 'pointer', overflow: 'hidden', background: BG }}>
                          {model.image_url ? (
                            <img src={model.image_url} alt={model.label} style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <div style={{ aspectRatio: '3/4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>👤</div>
                          )}
                          <div style={{ padding: '6px 8px', fontSize: '10px', color: MUTED, lineHeight: 1.3 }}>{model.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Model options */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '12px' }}>🎭 Model options</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Sel label="Gender" value={gender} onChange={v => { setGender(v); setPromptEdited(false) }} options={GENDERS} />
                  <Sel label="Age group" value={ageGroup} onChange={v => { setAgeGroup(v); setPromptEdited(false) }} options={AGE_GROUPS} optional />
                  <Sel label="Skin tone" value={skinTone} onChange={v => { setSkinTone(v); setPromptEdited(false) }} options={SKIN_TONES} optional />
                  <Sel label="Ethnicity / look" value={ethnicity} onChange={v => { setEthnicity(v); setPromptEdited(false) }} options={ETHNICITIES} optional />
                  <Sel label="Model style" value={modelStyle} onChange={v => { setModelStyle(v); setPromptEdited(false) }} options={MODEL_STYLES} optional />
                  <Sel label="Body type" value={bodyType} onChange={v => { setBodyType(v); setPromptEdited(false) }} options={BODY_TYPES} optional />
                </div>
              </div>

              {/* Scene options */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '12px' }}>🎬 Set the scene</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Sel label="Pose" value={pose} onChange={v => { setPose(v); setPromptEdited(false) }} options={POSES} optional />
                  <Sel label="Setting / background" value={setting} onChange={v => { setSetting(v); setPromptEdited(false) }} options={SETTINGS} optional />
                </div>
              </div>

              {/* Custom brief */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>✍️ Your brief — add specific details</div>
                <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '10px', lineHeight: 1.6 }}>
                  This is where you direct the shoot. Be specific — mention accessories, colours, mood, styling details. The more precise your brief, the better the output.
                </div>
                {/* Prompt tips */}
                <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px', fontSize: '11.5px', color: '#1E40AF', lineHeight: 1.7 }}>
                  <strong>💡 Prompt tips:</strong><br />
                  • Add accessories: "with matching gold earrings and heels"<br />
                  • Specify colours: "in a warm sunset colour palette"<br />
                  • Add mood: "relaxed and confident expression"<br />
                  • Avoid vague words like "nice" or "good" — be specific<br />
                  • For shoes/bags: "product prominently visible, centred"
                </div>
                <textarea value={customContext} onChange={e => { setCustomContext(e.target.value); setPromptEdited(false) }}
                  placeholder="e.g. Add matching gold jewellery and heels. Warm festive lighting. Model holding a small clutch bag."
                  rows={3}
                  style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '8px', padding: '9px 12px', fontSize: '12.5px', outline: 'none', resize: 'none', fontFamily: 'Space Grotesk, sans-serif', color: TEXT, background: WHITE, boxSizing: 'border-box' }} />
              </div>

              {/* Final prompt */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT }}>📋 Review the script — your final prompt</div>
                  <button onClick={() => { setPromptEdited(false); buildPrompt() }} style={{ fontSize: '11px', color: BLUE, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>↺ Rebuild</button>
                </div>
                <div style={{ fontSize: '11.5px', color: AMBER, marginBottom: '8px' }}>
                  ⚠️ Prompt quality directly determines your output. Review carefully before generating. Credits used are non-refundable.
                </div>
                <textarea value={finalPrompt} onChange={e => { setFinalPrompt(e.target.value); setPromptEdited(true) }}
                  rows={4}
                  style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '8px', padding: '9px 12px', fontSize: '12px', outline: 'none', resize: 'vertical', fontFamily: 'Space Grotesk, sans-serif', color: TEXT, background: BG, boxSizing: 'border-box', lineHeight: 1.6 }} />
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '6px' }}>You can edit this directly. Click "Rebuild" to regenerate from your selections.</div>
              </div>

              {/* Generate button */}
              <button onClick={handleGenerate} disabled={generating || !productImageUrl || !finalPrompt || totalCredits < 1}
                style={{ width: '100%', padding: '16px', borderRadius: '12px', border: 'none', fontSize: '16px', fontWeight: 900, cursor: generating || !productImageUrl || !finalPrompt || totalCredits < 1 ? 'not-allowed' : 'pointer', fontFamily: 'Space Grotesk, sans-serif', transition: 'all 0.15s', letterSpacing: '0.05em', background: generating ? BORDER : totalCredits < 1 ? '#FEF2F2' : GOLD, color: generating ? MUTED : totalCredits < 1 ? RED : NAVY }}>
                {generating ? '🎬 ' + generatingMsg : totalCredits < 1 ? '⚠️ No credits — top up below' : '🎬 ACTION! — 1 credit'}
              </button>
            </div>

            {/* RIGHT — Preview Stage */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid ' + BORDER, background: NAVY, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: RED }} />
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: GOLD }} />
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: GREEN }} />
                  <div style={{ flex: 1, textAlign: 'center', fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>Preview Stage</div>
                </div>

                <div style={{ minHeight: '480px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAFA', position: 'relative', padding: '16px' }}>
                  {generating ? (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid ' + GOLD, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>{generatingMsg}</div>
                      <div style={{ fontSize: '12px', color: MUTED }}>15-30 seconds typically</div>
                    </div>
                  ) : outputUrl ? (
                    <img src={outputUrl} alt="Generated" style={{ maxWidth: '100%', maxHeight: '600px', width: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                  ) : (
                    <div style={{ textAlign: 'center', color: MUTED }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎬</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>Your shot appears here</div>
                      <div style={{ fontSize: '12px', color: MUTED }}>Upload product, set the scene, hit ACTION!</div>
                    </div>
                  )}
                </div>

                {/* Watermark + download controls */}
                {outputUrl && (
                  <div style={{ padding: '16px', borderTop: '1px solid ' + BORDER }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '12px' }}>🖼️ Finish your shot</div>

                    {/* Watermark toggles */}
                    <div style={{ background: BG, borderRadius: '10px', padding: '12px 14px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Watermark options</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* Text watermark */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12.5px', color: TEXT }}>Shop name watermark — "{seller?.shop_name}"</span>
                          <button onClick={() => setWmText(t => !t)} style={{ width: '40px', height: '22px', borderRadius: '11px', background: wmText ? BLUE : '#D1D5DB', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: '2px', width: '18px', height: '18px', borderRadius: '50%', background: WHITE, transition: 'left 0.2s', left: wmText ? '20px' : '2px' }} />
                          </button>
                        </div>
                        {/* Logo watermark */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12.5px', color: TEXT }}>Logo watermark</span>
                          <button onClick={() => setWmLogo(l => !l)} style={{ width: '40px', height: '22px', borderRadius: '11px', background: wmLogo ? BLUE : '#D1D5DB', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: '2px', width: '18px', height: '18px', borderRadius: '50%', background: WHITE, transition: 'left 0.2s', left: wmLogo ? '20px' : '2px' }} />
                          </button>
                        </div>
                        {wmLogo && (
                          <div>
                            <button onClick={() => logoInputRef.current?.click()} style={{ padding: '6px 12px', borderRadius: '7px', background: BLUE, border: 'none', fontSize: '11.5px', fontWeight: 600, color: WHITE, cursor: 'pointer', marginBottom: '8px', fontFamily: 'Space Grotesk, sans-serif' }}>
                              {wmLogoImg ? '↑ Change logo' : '↑ Upload logo'}
                            </button>
                            <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = ev => setWmLogoImg(ev.target.result); r.readAsDataURL(f) } }} />
                            {wmLogoImg && (
                              <>
                                <div style={{ marginBottom: '6px' }}>
                                  <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>Logo position</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', maxWidth: '180px' }}>
                                    {['top-left','top-right','bottom-left','bottom-right','center'].map(pos => (
                                      <button key={pos} onClick={() => setWmPosition(pos)}
                                        style={{ padding: '4px 6px', borderRadius: '5px', border: '1px solid ' + (wmPosition === pos ? BLUE : BORDER), background: wmPosition === pos ? '#E7F3FF' : WHITE, fontSize: '10px', color: wmPosition === pos ? BLUE : MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif', gridColumn: pos === 'center' ? 'span 3' : 'span 1' }}>
                                        {pos === 'top-left' ? '↖' : pos === 'top-right' ? '↗' : pos === 'bottom-left' ? '↙' : pos === 'bottom-right' ? '↘' : '⊙ Center'}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>Logo size: {wmSize}px</div>
                                  <input type="range" min={40} max={200} value={wmSize} onChange={e => setWmSize(Number(e.target.value))} style={{ width: '100%' }} />
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {/* Opacity */}
                        <div>
                          <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>Watermark opacity: {wmOpacity}%</div>
                          <input type="range" min={5} max={40} value={wmOpacity} onChange={e => setWmOpacity(Number(e.target.value))} style={{ width: '100%' }} />
                        </div>
                      </div>
                    </div>

                    {/* Download buttons */}
                    <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Take your print</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                      {[
                        { label: '⬇ Clean — no watermark', mode: 'clean', bg: BG, color: TEXT },
                        { label: '⬇ Text watermark', mode: 'text', bg: BG, color: TEXT },
                        { label: '⬇ Logo only', mode: 'logo', bg: BG, color: TEXT },
                        { label: '⬇ Text + Logo', mode: 'both', bg: BLUE, color: WHITE },
                      ].map(btn => (
                        <button key={btn.mode} onClick={() => applyWatermark(btn.mode)}
                          style={{ padding: '10px', borderRadius: '9px', border: '1px solid ' + BORDER, background: btn.bg, color: btn.color, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Studio credits + packs ────────────────────────────────────── */}
          <div id="studio-packs" style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BORDER, background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 800, color: WHITE }}>Top up the studio</div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>Buy more, save more — credits never expire</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>You have</span>
                <span style={{ fontSize: '18px', fontWeight: 900, color: WHITE }}>{totalCredits}</span>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>credits</span>
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }} className="packs-grid">
                {packs.map(pack => (
                  <div key={pack.name} style={{ borderRadius: '12px', border: '2px solid ' + (pack.best ? GOLD : BORDER), padding: '16px', textAlign: 'center', position: 'relative', background: pack.best ? '#FFFBEB' : WHITE }}>
                    {pack.best && (
                      <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: GOLD, color: NAVY, fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>⭐ Most popular</div>
                    )}
                    <div style={{ fontSize: '13px', fontWeight: 800, color: TEXT, marginBottom: '4px' }}>{pack.name}</div>
                    <div style={{ fontSize: '24px', fontWeight: 900, color: pack.best ? AMBER : BLUE, marginBottom: '2px' }}>{pack.credits}</div>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '10px' }}>shots</div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, marginBottom: '2px' }}>{sym}{pack.price}</div>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '6px' }}>{sym}{(pack.price / pack.credits).toFixed(1)}/shot</div>
                    {pack.saving && (
                      <div style={{ fontSize: '11px', fontWeight: 700, color: GREEN, marginBottom: '10px' }}>Save {sym}{pack.saving}</div>
                    )}
                    {!pack.saving && <div style={{ marginBottom: '10px', height: '17px' }} />}
                    <button onClick={() => handleBuyPack(pack)} disabled={payingPack === pack.name || payLoading}
                      style={{ width: '100%', padding: '9px', borderRadius: '8px', border: 'none', background: pack.best ? GOLD : NAVY, color: pack.best ? NAVY : WHITE, fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif', opacity: payingPack === pack.name ? 0.6 : 1 }}>
                      {payingPack === pack.name ? 'Redirecting...' : 'Book it →'}
                    </button>
                  </div>
                ))}
              </div>

              {/* Purchase breakdown */}
              <div style={{ background: BG, borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11.5px', color: MUTED, lineHeight: 1.7 }}>
                  <strong style={{ color: TEXT }}>How credits work:</strong> 1 credit = 1 professional model photo.
                  Free credits ({plan === 'business' ? '10' : plan === 'pro' ? '3' : '0'}/month with your plan) are used first, then purchased credits.
                  Purchased credits never expire. Failed generations are refunded automatically.
                  All purchases include a digital invoice sent to your email.
                </div>
              </div>
            </div>

            {/* Purchase history */}
            <div style={{ borderTop: '1px solid ' + BORDER }}>
              <div style={{ padding: '12px 20px', background: BG, borderBottom: '1px solid ' + BORDER }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT }}>Purchase history</div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                <div style={{ fontSize: '12px', color: MUTED, textAlign: 'center', padding: '12px' }}>No purchases yet — credit pack invoices appear here after payment.</div>
              </div>
            </div>
          </div>

          {/* ── Past productions ─────────────────────────────────────────── */}
          <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>🎞️ Past productions</div>
              <div style={{ fontSize: '12px', color: MUTED }}>{history.length} shots</div>
            </div>
            {historyLoading ? (
              <div style={{ padding: '32px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>Loading...</div>
            ) : history.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎬</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>No productions yet</div>
                <div style={{ fontSize: '12px', color: MUTED }}>Your generated shots will appear here</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', padding: '16px' }}>
                {history.map(gen => (
                  <div key={gen.id} style={{ borderRadius: '10px', border: '1px solid ' + BORDER, overflow: 'hidden', cursor: gen.output_image_url ? 'pointer' : 'default' }} onClick={() => gen.output_image_url && setOutputUrl(gen.output_image_url)}>
                    {gen.output_image_url ? (
                      <img src={gen.output_image_url} alt="generation" style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ aspectRatio: '3/4', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                        {gen.status === 'failed' ? '❌' : gen.status === 'processing' ? '⏳' : '🎬'}
                      </div>
                    )}
                    <div style={{ padding: '6px 8px' }}>
                      <div style={{ fontSize: '10.5px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gen.category || 'Generation'}</div>
                      <div style={{ fontSize: '10px', color: gen.status === 'completed' ? GREEN : gen.status === 'failed' ? RED : AMBER }}>{gen.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      <style>{`
        @media(max-width:900px){.studio-grid{grid-template-columns:1fr!important}.samples-grid{grid-template-columns:repeat(3,1fr)!important}.packs-grid{grid-template-columns:repeat(2,1fr)!important}}
        @media(max-width:560px){.how-grid{grid-template-columns:1fr!important}.samples-grid{grid-template-columns:repeat(2,1fr)!important}.packs-grid{grid-template-columns:1fr!important}}
      `}</style>
    </div>
  )
}

export default AIModelPhotos